interface StrategySection {
  layer: string;
  imageUrl?: string;
  label: string;
  title: string;
  description: string;
  strategicValue?: string;
  materials?: string;
  reasoning?: string;
  riskControl?: string;
  stabilization?: string;
}

interface MaterialStrategyBlockProps {
  content: {
    title?: string;
    subtitle?: string;
    showDivider?: boolean;
    sections?: StrategySection[];
  };
}

export function MaterialStrategyBlock({ content }: MaterialStrategyBlockProps) {
  if (!content.sections?.length) return null;

  return (
    <section className="py-20">
      <div className="mx-auto max-w-[1200px] px-6 mb-16">
        {content.showDivider && <div className="mb-12 h-px w-16 bg-border" />}
        {content.title && (
          <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
            {content.title}
          </h2>
        )}
        {content.subtitle && (
          <p className="font-sans text-sm text-muted-foreground max-w-[700px]">
            {content.subtitle}
          </p>
        )}
      </div>

      <div className="space-y-0">
        {content.sections.map((section, i) => {
          const reversed = i % 2 !== 0;
          return (
            <div
              key={i}
              className={`grid grid-cols-1 md:grid-cols-2 min-h-[500px] ${
                reversed ? "md:direction-rtl" : ""
              }`}
              style={reversed ? { direction: "rtl" } : undefined}
            >
              {/* Image */}
              <div
                className="relative overflow-hidden min-h-[300px] md:min-h-[500px]"
                style={reversed ? { direction: "ltr" } : undefined}
              >
                {section.imageUrl && (
                  <img
                    src={section.imageUrl}
                    alt={section.title}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
              </div>

              {/* Content */}
              <div
                className="flex flex-col justify-center px-8 md:px-16 py-12 bg-background"
                style={reversed ? { direction: "ltr" } : undefined}
              >
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  {section.label}
                </p>
                <h3 className="font-serif text-xl md:text-2xl text-foreground mb-4">
                  {section.title}
                </h3>
                <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-6">
                  {section.description}
                </p>
                {section.strategicValue && (
                  <div>
                    <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-foreground/60 mb-2">
                      Strategic Value
                    </h4>
                    <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                      {section.strategicValue}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
